import { api } from '@packages/backend/convex/_generated/api';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { useEffect } from 'react';
import { ErrorAlert } from '@/components/ErrorAlert';
import { LoadingIcon } from '@/components/LoadingIcon';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { env } from '@/lib/env';
import { AgentBadge } from './AgentBadge';
import { ConversationDetailsPanel } from './ConversationDetailsPanel';
import { HumanReplyComposer } from './HumanReplyComposer';
import { PendingAdminRequestPanel } from './PendingAdminRequestPanel';
import { Transcript } from './Transcript';

const SHOW_SIMULATE = env.VITE_STAGE !== 'production';

export const Route = createFileRoute('/a/conversations/$conversationId')({
  component: ConversationDetailsPage,
});

function ConversationDetailsPage() {
  const { conversationId } = Route.useParams();
  const convex = useConvex();
  const details = useQuery(api.conversations.getConversation, { conversationId });
  const context = useQuery(api.conversations_admin.getConversationContext, {
    conversationId,
  });
  const conversation = details.data?.conversation ?? null;
  const threadId = conversation?.agentThreadId ?? null;
  const pendingAdminRequest = useQuery(
    api.conversations_admin.getPendingAdminRequestForThread,
    threadId ? { threadId } : 'skip',
  );

  const hasUnseenActivity = conversation?.hasUnseenActivity ?? false;
  useEffect(() => {
    if (!hasUnseenActivity) return;
    void convex.mutation(api.conversations_admin.markConversationSeen, { conversationId });
  }, [convex, conversationId, hasUnseenActivity]);

  if (details.isPending || context.isPending) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center">
        <LoadingIcon />
      </div>
    );
  }

  if (details.isError) {
    return <ErrorAlert message={details.error.message} title="Error loading conversation" />;
  }
  if (context.isError) {
    return <ErrorAlert message={context.error.message} title="Error loading conversation" />;
  }

  if (!details.data || !conversation) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Conversation not found</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  const agentInstructions = context.data?.agentInstructions ?? '';
  const restaurantUser = context.data?.restaurantUser ?? null;
  const restaurants = context.data?.restaurants ?? [];
  const customerLabel =
    conversation.kind === 'sales'
      ? `Anonymous ${conversation.visitorIdentifier.slice(0, 8)}`
      : 'Restaurant user';

  return (
    <div className="grid h-full min-h-0 items-stretch gap-4 xl:grid-cols-[minmax(0,1fr)_420px] xl:gap-6">
      <Card className="flex h-full min-h-0 flex-col gap-0 overflow-hidden py-0">
        <CardHeader className="flex flex-row flex-wrap items-center gap-2 px-5 pt-4 pb-4">
          <AgentBadge agentInstructions={agentInstructions} kind={conversation.kind} />
        </CardHeader>
        <CardContent className="subtle-scrollbar min-h-0 flex-1 overflow-y-auto border-t px-5 py-5">
          <Transcript conversation={conversation} />
        </CardContent>
        {pendingAdminRequest.data && (
          <PendingAdminRequestPanel
            adminRequestId={pendingAdminRequest.data._id}
            question={pendingAdminRequest.data.question}
          />
        )}
      </Card>

      <Card className="flex h-full min-h-0 flex-col gap-0 overflow-hidden py-0">
        <CardContent className="flex min-h-0 flex-1 flex-col px-5 py-5">
          <ConversationDetailsPanel
            customerLabel={customerLabel}
            restaurants={restaurants}
            restaurantUser={restaurantUser}
          />
        </CardContent>
        {SHOW_SIMULATE && <HumanReplyComposer conversationId={conversationId} />}
      </Card>
    </div>
  );
}
